import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, User, UserPlus, Trash2, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useMemo } from "react";
import { useTenantUsers } from "@/hooks/useTenantUsers";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { CreateUserForm } from "./CreateUserForm";
import { BulkUserActionsToolbar } from "./BulkUserActionsToolbar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

interface TenantUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: {
    id: string;
    name: string;
    slug: string;
  } | null;
  userCount: number;
}

export function TenantUsersDialog({ open, onOpenChange, tenant, userCount }: TenantUsersDialogProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'tenant_admin'>('all');
  
  const { 
    users, 
    isLoading, 
    updateUserRole, 
    removeUser, 
    bulkUpdateRoles, 
    bulkRemoveUsers 
  } = useTenantUsers(tenant?.id || null);
  
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
  } = useBulkSelection();

  // Filter users based on search and role filter
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        user.profile.first_name?.toLowerCase().includes(searchLower) ||
        user.profile.last_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower);
      
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      
      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, roleFilter]);

  const handleClose = () => {
    setShowCreateForm(false);
    clearSelection();
    setSearchQuery('');
    setRoleFilter('all');
    onOpenChange(false);
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
  };

  const handleRemoveUser = (userId: string) => {
    removeUser.mutate(userId, {
      onSuccess: () => {
        setUserToRemove(null);
        clearSelection();
      },
    });
  };

  const handleBulkUpdateRole = (role: 'user' | 'tenant_admin') => {
    bulkUpdateRoles.mutate({ userIds: selectedIds, newRole: role }, {
      onSuccess: () => clearSelection(),
    });
  };

  const handleBulkRemove = () => {
    bulkRemoveUsers.mutate(selectedIds, {
      onSuccess: () => clearSelection(),
    });
  };

  const handleSelectAll = () => {
    if (selectedCount === filteredUsers.length) {
      clearSelection();
    } else {
      selectAll(filteredUsers.map(u => u.user_id));
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'tenant_admin':
        return <Shield className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "destructive" => {
    return role === 'tenant_admin' ? 'default' : 'secondary';
  };

  if (!tenant) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Manage Users - {tenant.name}</DialogTitle>
          </DialogHeader>

          {showCreateForm ? (
            <CreateUserForm
              tenantId={tenant.id}
              tenantName={tenant.name}
              onSuccess={handleCreateSuccess}
              onCancel={() => setShowCreateForm(false)}
            />
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              {/* User Statistics */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{users.length}</div>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">
                      {users.filter(u => u.role === 'tenant_admin').length}
                    </div>
                    <p className="text-xs text-muted-foreground">Admins</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">
                      {users.filter(u => u.role === 'user').length}
                    </div>
                    <p className="text-xs text-muted-foreground">Users</p>
                  </CardContent>
                </Card>
              </div>

              {/* Invite New User Button */}
              <Button onClick={() => setShowCreateForm(true)} className="w-full mb-6">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite New User
              </Button>

              {/* Search and Filter */}
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={roleFilter} onValueChange={(value: any) => setRoleFilter(value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="tenant_admin">Admins</SelectItem>
                    <SelectItem value="user">Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bulk Actions Toolbar */}
              {selectedCount > 0 && (
                <BulkUserActionsToolbar
                  selectedCount={selectedCount}
                  onClearSelection={clearSelection}
                  onBulkUpdateRole={handleBulkUpdateRole}
                  onBulkRemove={handleBulkRemove}
                  isLoading={bulkUpdateRoles.isPending || bulkRemoveUsers.isPending}
                />
              )}

              {/* Users List */}
              <div className="flex-1 overflow-y-auto space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox
                    checked={selectedCount === filteredUsers.length && filteredUsers.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                  <h3 className="text-sm font-medium">
                    Users ({filteredUsers.length})
                  </h3>
                </div>

                {isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6 text-center text-sm text-muted-foreground">
                      {searchQuery || roleFilter !== 'all' 
                        ? 'No users found matching your filters.'
                        : 'No users found. Invite your first user to get started.'}
                    </CardContent>
                  </Card>
                ) : (
                  filteredUsers.map((user) => {
                    const userName = user.profile 
                      ? `${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim() || 'Unnamed User'
                      : 'Unnamed User';

                    return (
                      <Card key={user.user_id} className={isSelected(user.user_id) ? 'ring-2 ring-primary' : ''}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <Checkbox
                                checked={isSelected(user.user_id)}
                                onCheckedChange={() => toggleSelect(user.user_id)}
                              />
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                                {getRoleIcon(user.role)}
                              </div>
                              <div>
                                <div className="font-medium">
                                  {userName}
                                </div>
                                {user.email && (
                                  <div className="text-xs text-muted-foreground">
                                    {user.email}
                                  </div>
                                )}
                                <Badge variant={getRoleBadgeVariant(user.role)} className="mt-1">
                                  {user.role === 'tenant_admin' ? 'Tenant Admin' : 'User'}
                                </Badge>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Select
                                value={user.role}
                                onValueChange={(newRole) =>
                                  updateUserRole.mutate({
                                    userId: user.user_id,
                                    newRole: newRole as 'user' | 'tenant_admin',
                                  })
                                }
                              >
                                <SelectTrigger className="w-[160px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="user">User</SelectItem>
                                  <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
                                </SelectContent>
                              </Select>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setUserToRemove(user.user_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!userToRemove} onOpenChange={(open) => !open && setUserToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this user from the tenant? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => userToRemove && handleRemoveUser(userToRemove)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
